import { nullishFilter } from '@caresend/utils';
import dayjs from 'dayjs';

/**
 * A list of month options with the full month name
 */
const MONTH_OPTIONS = [
    { name: 'January', value: 0 },
    { name: 'February', value: 1 },
    { name: 'March', value: 2 },
    { name: 'April', value: 3 },
    { name: 'May', value: 4 },
    { name: 'June', value: 5 },
    { name: 'July', value: 6 },
    { name: 'August', value: 7 },
    { name: 'September', value: 8 },
    { name: 'October', value: 9 },
    { name: 'November', value: 10 },
    { name: 'December', value: 11 },
];
/**
 * A list of month options with the month name shortened to 3 characters
 */
const SHORT_MONTHS_OPTIONS = MONTH_OPTIONS.map((item) => ({
    name: item.name.slice(0, 3),
    value: item.value,
}));
/**
 * A map of month values to their full month name
 */
const SHORT_MONTH_MAP = SHORT_MONTHS_OPTIONS.reduce((acc, { value, name }) => {
    acc[value] = name;
    return acc;
}, {});
/**
 * A map of month values to their full month name
 */
const MONTH_MAP = MONTH_OPTIONS.reduce((acc, { value, name }) => {
    acc[value] = name;
    return acc;
}, {});
/**
 * javascript's `%` operator is not a true modulo operator since it computes
 * the remainder of a division operation. This means that `-1 % 12` is `-1`
 * instead of `11`. This function corrects this behavior, which is more apt for
 * our back-pagination button on DatePicker__header-month-navigation
 *
 * @param n - The number to mod
 * @param m - The modulus
 * @returns n mod m
 */
const properMod = (n, m) => ((n % m) + m) % m;
// Validation Regex helpers:
/** List of partially valid values accepted per address part (month, day, year) */
const partialValidRegex = [/^([0-9]|0[1-9]|1[0-2])?$/, /^(0|0[1-9]|[1-2]\d|3[0-1])?$/, /^([1-9]\d{0,3})?$/];
/** List of valid values accepted per address part (month, day, year) */
const validRegexes = [/^(0[1-9]|1[0-2])$/, /^(0[1-9]|[12]\d|3[01])$/, /^([1-9]\d{3})$/];
/** Regex for validating the full date input */
const dateRegex = /^(0[1-9]|1[0-2])(\/)(0[1-9]|[12]\d|3[01])(\/)[1-2]\d{3}$/;
/** Regex for post-formatted date with spaces */
const dateRegexWithSpaces = /^(0[1-9]|1[0-2]) (\/) (0[1-9]|[12]\d|3[01]) (\/) [1-2]\d{3}$/;
/**
 * get whether all the 'parts' from a date array (split from '/' delimited string, could be of length [1,3]) is valid
 * @param parts - The parts of the date
 * @returns Whether all the parts are valid
 */
const partiallyValidParts = (parts) => parts.every((p, i) => { var _a, _b; return i === (parts.length - 1) ? (_a = partialValidRegex[i]) === null || _a === void 0 ? void 0 : _a.test(p) : (_b = validRegexes[i]) === null || _b === void 0 ? void 0 : _b.test(p); });
const slashCount = (value) => value.split('/').length - 1;
/* eslint-disable no-param-reassign */
/**
 * Get the helper functions for the text input auto-formatting.
 * Must pass in different reactive values that these functions depend on.
 *
 * @param inputTag - The input tag ref
 * @param lastValidValue - The last valid value ref
 * @param isClearVisible - Whether the clear button is visible ref
 * @param inputHandler - The input handler function that updates the date, emits the date etc
 * @returns The helper functions
 */
const getTextInputHelpers = (inputTag, lastValidValue, isClearVisible, inputHandler) => {
    /**
       * Helper function to handle invalid state in our custom input
       * @returns true if consuming function should return
      */
    const handleTextInputInvalidStateHelper = (input, slashCountValue) => {
        if (!/^(\d|\/)*$/.test(input.value)
            || input.value === '0/'
            || input.value === '/'
            || slashCountValue > 2
            || (input.value.at(-1) === '/' && input.value.at(-2) === '/')) {
            input.value = lastValidValue.value;
            return true;
        }
        // If the input is valid, we need to show the clear button
        isClearVisible.value = true;
        return false;
    };
    /**
       * Helper function to handle inputs without slashes in our custom input
       * @returns true if consuming function should return
      */
    const handleNoSlashTextInputHelper = (input) => {
        var _a;
        // if single digit is a valid month but adding another number would invalidate,
        // zero pad the month and add a slash to move to the day
        if (input.value.length === 1 && Number(input.value[0]) > 1) {
            input.value = `0${input.value}/`;
            lastValidValue.value = input.value;
            return true;
        }
        // if the input is 2 characters and not a valid month, format input into month and day
        // first (month) digit must be 0 padded, second (day) digit must be 0 padded iff > 3 (otherwise invalid)
        if (input.value.length === 2 && !((_a = validRegexes[0]) === null || _a === void 0 ? void 0 : _a.test(input.value))) {
            const isNumber = !Number.isNaN(Number(input.value));
            if (!isNumber && Number(input.value[0]) === 0) {
                input.value = lastValidValue.value;
                return true;
            }
            const dayPart = Number(input.value[1]) > 3
                ? `0${input.value[1]}/`
                : input.value[1];
            input.value = `0${input.value[0]}/${dayPart}`;
            // if the input is 2 characters, zero pad the month and add a slash to move to the day
        }
        else if (input.value.length === 2) {
            input.value = `${input.value}/`;
            lastValidValue.value = input.value;
            return true;
        }
        // Need to make sure that, especially when deleting and being in a bad state,
        // we still format the day correctly
        if (input.value.length === 3) {
            const dayPart = Number(input.value[2]) > 3
                ? `0${input.value[2]}/`
                : input.value[2];
            input.value = `${input.value.slice(0, 2)}/${dayPart}`;
            lastValidValue.value = input.value;
            return true;
        }
        return false;
    };
    /**
       * Helper function to handle inputs with a single slash in our custom input
       * @returns true if consuming function should return
      */
    const handleSingleSlashTextInputHelper = (input, parts) => {
        var _a, _b, _c;
        // if the second part is a single digit and greater than 3, zero pad it and move on to year
        if (parts.length === 2 && ((_a = parts[1]) === null || _a === void 0 ? void 0 : _a.length) === 1 && Number(parts[1]) > 3) {
            input.value = `${input.value.slice(0, 2)}/0${parts[1]}/`;
            lastValidValue.value = input.value;
            return true;
        }
        // if the day part is wrong at this point, we know that it's a '3' followed by nothing or some other number
        // if single 3, leave as is. If 3 followed by a number higher than 1, then zero pad and move on to year
        if (!((_b = validRegexes[1]) === null || _b === void 0 ? void 0 : _b.test(parts[1]))) {
            const wrongMonth = (_c = parts[1]) !== null && _c !== void 0 ? _c : '';
            if (wrongMonth.length === 2 && wrongMonth[0] === '3' && Number(wrongMonth[1]) > 1) {
                input.value = `${input.value.slice(0, 2)}/0${wrongMonth[0]}/${wrongMonth[1]}`;
                lastValidValue.value = input.value;
                return true;
            }
            if (wrongMonth.length === 3) {
                input.value = `${input.value.slice(0, 2)}/${wrongMonth.slice(0, 2)}/${wrongMonth[2]}`;
                lastValidValue.value = input.value;
                return true;
            }
        }
        // if the input is valid and the length is 5, append a slash to the end of the input
        if (input.value.length === 5) {
            input.value = `${input.value}/`;
            lastValidValue.value = input.value;
            return true;
        }
        return false;
    };
    /**
       * Helper function to handle misc logic in our custom input
       * @returns true if consuming function should return
      */
    const handleMiscTextInputLogicHelper = (input, parts) => {
        var _a, _b, _c, _d;
        const newParts = nullishFilter(parts.at(-1)) && ((_a = parts.at(-1)) === null || _a === void 0 ? void 0 : _a.length) === 0 ? parts.slice(0, -1) : parts;
        // zero pad any parts that need to be zero padded
        if (newParts.length < 3 && ((_b = newParts.at(-1)) === null || _b === void 0 ? void 0 : _b.length) === 1 && input.value.at(-1) === '/') {
            newParts[newParts.length - 1] = (_c = newParts.at(-1)) === null || _c === void 0 ? void 0 : _c.padStart(2, '0');
        }
        // If we have a dangling slash or the last part is not a valid state
        if (input.value.at(-1) === '/'
            && !((_d = validRegexes[newParts.length - 1]) === null || _d === void 0 ? void 0 : _d.test(newParts === null || newParts === void 0 ? void 0 : newParts.at(-1)))
            && !partiallyValidParts(newParts)) {
            input.value = lastValidValue.value;
            return true;
        }
        // Update values with our new parts
        input.value = `${newParts.join('/')}${input.value.at(-1) === '/' ? '/' : ''}`;
        lastValidValue.value = input.value;
        return false;
    };
    /** Helper function to handle valid input dates in our custom input and update our date value */
    const handleValidInputDateHelper = (input) => {
        if (!dateRegex.test(input.value))
            return;
        let dateValue = dayjs(input.value).toDate();
        const maybeDifferentDate = dayjs(dateValue).format('MM/DD/YYYY');
        if (inputTag.value && input.value !== maybeDifferentDate) {
            dateValue = dayjs(dateValue).toDate();
            inputTag.value.value = maybeDifferentDate;
        }
        inputHandler(dateValue);
    };
    return {
        handleTextInputInvalidStateHelper,
        handleNoSlashTextInputHelper,
        handleSingleSlashTextInputHelper,
        handleMiscTextInputLogicHelper,
        handleValidInputDateHelper,
    };
};
/* eslint-enable no-param-reassign */

export { MONTH_MAP, MONTH_OPTIONS, SHORT_MONTHS_OPTIONS, SHORT_MONTH_MAP, dateRegex, dateRegexWithSpaces, getTextInputHelpers, partialValidRegex, partiallyValidParts, properMod, slashCount, validRegexes };
